// Deal management API client. Exposes the full surface required by every
// follow-up change (list / get / mutate / invoices / shipments / catalog) so
// that downstream changes only need to fill in implementation, never edit
// signatures.
//
// Real backend is not shipped yet: when REACT_APP_USE_MOCK_DEALS is 'true'
// (current default) every call dispatches to the mock module that persists
// to localStorage. When false, calls go to dealsAPI.real.ts which currently
// throws `not implemented` — wired up by the final swap-deal-mock-to-real-api
// change.

import type { ProductGroup } from '../pages/deals/types/catalog'
import type { Deal, Shipment, ShipmentLeg } from '../pages/deals/types/deal'
import type { Invoice, InvoiceItem } from '../pages/deals/types/invoice'

import type {
  CancelDealReqBodyDto,
  CreateDealReqBodyDto,
  CreateInvoiceReqBodyDto,
  CreateShipmentReqBodyDto,
  LineItemAssignmentsDto,
  LineItemDto,
  ListPaginatedDealsResBodyDto,
  SendInvoiceReqBodyDto,
  UpdateDealReqBodyDto,
  UpdateInvoiceReqBodyDto,
  UpdateShipmentReqBodyDto,
  VoidInvoiceReqBodyDto,
} from './hey-api/types.gen'
import * as mockApi from '../pages/deals/__mock__/dealsApi'
import * as realApi from './dealsAPI.real'

// ─── Entity aliases ────────────────────────────────────────────────────
// Until the backend hey-api spec lands we pin all entity types to the
// prototype's domain shapes; downstream changes operate on these.
export type DealEntity = Deal
export type InvoiceEntity = Invoice
export type ShipmentEntity = Shipment
export type ShipmentLegEntity = ShipmentLeg
export type InvoiceItemEntity = InvoiceItem
export type ProductGroupEntity = ProductGroup

// ─── Query / body shapes ───────────────────────────────────────────────

export interface ListDealsQuery {
  page?: number
  pagePerRecordCount?: number
  status?: string
  payment?: string
  search?: string
}

export type ListPaginatedDealsResult = ListPaginatedDealsResBodyDto

export type CreateDealReqBody = CreateDealReqBodyDto

export type UpdateDealReqBody = UpdateDealReqBodyDto

export type LineItemReqBody = LineItemDto

export type LineItemAssignments = LineItemAssignmentsDto

export type CancelDealReqBody = CancelDealReqBodyDto

export type CreateManualInvoiceReqBody = CreateInvoiceReqBodyDto

export type UpdateInvoiceReqBody = UpdateInvoiceReqBodyDto

export type VoidInvoiceReqBody = VoidInvoiceReqBodyDto

export type SendInvoiceReqBody = SendInvoiceReqBodyDto

export type CreateShipmentReqBody = CreateShipmentReqBodyDto

export type UpdateShipmentReqBody = UpdateShipmentReqBodyDto

// ─── Dispatch ──────────────────────────────────────────────────────────

const useMock = process.env.REACT_APP_USE_MOCK_DEALS === 'true'
const adapter = useMock ? mockApi : realApi

// ─── Deals ─────────────────────────────────────────────────────────────

export const listDeals = (query: ListDealsQuery = {}): Promise<ListPaginatedDealsResult> =>
  adapter.listDeals(query)

export const getDeal = (dealId: string): Promise<DealEntity> => adapter.getDeal(dealId)

export const createDeal = (body: CreateDealReqBody): Promise<DealEntity> => adapter.createDeal(body)

export const updateDeal = (dealId: string, body: UpdateDealReqBody): Promise<DealEntity> =>
  adapter.updateDeal(dealId, body)

export const archiveDeal = (dealId: string): Promise<DealEntity> => adapter.archiveDeal(dealId)

export const unarchiveDeal = (dealId: string): Promise<DealEntity> => adapter.unarchiveDeal(dealId)

export const cancelDeal = (dealId: string, body: CancelDealReqBody): Promise<DealEntity> =>
  adapter.cancelDeal(dealId, body)

export const uncancelDeal = (dealId: string): Promise<DealEntity> => adapter.uncancelDeal(dealId)

// ─── Invoices ──────────────────────────────────────────────────────────

export const listInvoices = (dealId: string): Promise<InvoiceEntity[]> =>
  adapter.listInvoices(dealId)

export const getInvoice = (dealId: string, invoiceId: string): Promise<InvoiceEntity> =>
  adapter.getInvoice(dealId, invoiceId)

export const createManualInvoice = (
  dealId: string,
  body: CreateManualInvoiceReqBody,
): Promise<InvoiceEntity> => adapter.createManualInvoice(dealId, body)

export const generateNextInvoice = (dealId: string): Promise<InvoiceEntity> =>
  adapter.generateNextInvoice(dealId)

export const updateInvoice = (
  dealId: string,
  invoiceId: string,
  body: UpdateInvoiceReqBody,
): Promise<InvoiceEntity> => adapter.updateInvoice(dealId, invoiceId, body)

export const voidInvoice = (
  dealId: string,
  invoiceId: string,
  body?: VoidInvoiceReqBody,
): Promise<InvoiceEntity> => adapter.voidInvoice(dealId, invoiceId, body)

export const restoreInvoice = (dealId: string, invoiceId: string): Promise<InvoiceEntity> =>
  adapter.restoreInvoice(dealId, invoiceId)

export const sendInvoice = (
  dealId: string,
  invoiceId: string,
  body: SendInvoiceReqBody,
  idempotencyKey: string,
): Promise<InvoiceEntity> => adapter.sendInvoice(dealId, invoiceId, body, idempotencyKey)

export const getInvoicePdfBlob = (dealId: string, invoiceId: string): Promise<Blob> =>
  adapter.getInvoicePdfBlob(dealId, invoiceId)

// ─── Shipments ─────────────────────────────────────────────────────────

export const listShipments = (dealId: string): Promise<ShipmentEntity[]> =>
  adapter.listShipments(dealId)

export const createShipment = (
  dealId: string,
  body: CreateShipmentReqBody,
): Promise<ShipmentEntity> => adapter.createShipment(dealId, body)

export const updateShipment = (
  dealId: string,
  shipmentId: string,
  body: UpdateShipmentReqBody,
): Promise<ShipmentEntity> => adapter.updateShipment(dealId, shipmentId, body)

export const deleteShipment = (dealId: string, shipmentId: string): Promise<void> =>
  adapter.deleteShipment(dealId, shipmentId)

// ─── Catalog ───────────────────────────────────────────────────────────

export const listProducts = (): Promise<ProductGroupEntity[]> => adapter.listProducts()
